import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Layers, Zap, ArrowUpRight, Plus, Check, ChevronDown, Info } from "lucide-react";

const COVERAGE = {
  full: { label: "Full coverage", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial: { label: "Partial coverage", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  external: { label: "External rubric", cls: "bg-gray-100 text-gray-600 border-gray-200" },
};
const EFFORT_LABEL = { S: "Small", M: "Medium", L: "Large" };

export const RiskCalibration = ({ riskId, user, onSeeded }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [playbooks, setPlaybooks] = useState([]);
  const [seeding, setSeeding] = useState(null);
  const [seeded, setSeeded] = useState({});
  const canSeed = hasPermission(user, "playbook.assign");

  useEffect(() => {
    let active = true;
    api
      .get(`/risks/${riskId}/calibration`)
      .then(({ data }) => active && setData(data))
      .catch(() => active && setErr(true));
    return () => {
      active = false;
    };
  }, [riskId]);

  const loadPlaybooks = (themeId) => {
    api
      .get(`/playbooks?theme_id=${themeId}`)
      .then(({ data }) => setPlaybooks(data.filter((p) => p.origin !== "ekselens360_csi")))
      .catch(() => {});
  };

  useEffect(() => {
    if (data?.theme_id && canSeed) loadPlaybooks(data.theme_id);
  }, [data?.theme_id]);

  const applyMove = async (move, idx, targetPlaybookId) => {
    setSeeding(idx);
    try {
      const { data: res } = await api.post(`/risks/${riskId}/seed-playbook-from-move`, {
        move_name: move.name,
        framework: move.framework,
        target_playbook_id: targetPlaybookId || null,
      });
      toast.success(
        res.mode === "appended"
          ? `Step added to “${res.title}”`
          : "Playbook seeded & assigned to you"
      );
      setSeeded((s) => ({ ...s, [idx]: true }));
      if (data?.theme_id) loadPlaybooks(data.theme_id);
      onSeeded?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not seed playbook");
    } finally {
      setSeeding(null);
    }
  };

  if (err) return null;
  if (!data) return null;

  const cal = data.calibration;
  const cov = cal ? COVERAGE[cal.coverage] || COVERAGE.external : null;
  const refs = cal ? [...(cal.primary || []), ...(cal.supporting || [])] : [];

  // Only surface the EKSELENS360 sub-brand when this risk actually maps to a
  // framework. With no link, the panel adds noise — so we hide it entirely.
  if (refs.length === 0) return null;

  return (
    <div className="mt-8" data-testid="risk-calibration">
      <div className="flex items-center justify-between">
        <h3 className="ek-eyebrow flex items-center gap-1">
          <Layers size={12} /> EKSELENS360 calibration
          <span className="group relative inline-flex">
            <Info size={11} className="cursor-help text-ek-ink/30 transition-colors group-hover:text-ek-purple" data-testid="calibration-rationale" />
            <span
              role="tooltip"
              className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 w-64 -translate-x-1/2 rounded-md bg-ek-ink px-3 py-2 text-left text-xs font-normal normal-case leading-relaxed tracking-normal text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
            >
              EKSELENS360 is Ekselens&apos; framework library. This risk maps to the framework(s) below,
              so ERIO shows which methodology &amp; control set governs it — and surfaces pre-validated
              remediation moves drawn from that framework. Shown only when a direct mapping exists.
            </span>
          </span>
        </h3>
        {cov && (
          <span className={`border px-2 py-0.5 text-[0.65rem] font-semibold ${cov.cls}`} data-testid="calibration-coverage">
            {cov.label}
          </span>
        )}
      </div>

      <div className="mt-3 space-y-2">
        {refs.map((r, i) => (
          <div
            key={`${r.service_slug}-${i}`}
            data-testid="calibration-framework"
            className="border border-black/10 bg-white p-3"
          >
            <p className="text-sm font-semibold text-ek-ink">
              {r.service_slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            </p>
            {r.use && <p className="mt-0.5 text-xs text-ek-ink/65">{r.use}</p>}
            <div className="mt-1.5 flex flex-wrap gap-1">
              {(r.dimensions || []).map((d) => (
                <span key={d} className="bg-ek-canvas px-1.5 py-0.5 text-[0.65rem] font-medium text-ek-ink/60">
                  {d.replace(/_/g, " ")}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {data.proposed_moves?.length > 0 && (
        <div className="mt-4">
          <h4 className="ek-eyebrow mb-2 flex items-center gap-1 text-[0.65rem]">
            <Zap size={11} /> Pre-validated remediation moves
          </h4>
          <div className="space-y-1.5" data-testid="calibration-moves">
            {data.proposed_moves.map((m, i) => (
              <div key={i} className="flex items-start justify-between gap-2 border border-ek-purple/15 bg-ek-purple/[0.04] p-2.5">
                <div className="flex items-start gap-1.5">
                  <ArrowUpRight size={13} className="mt-0.5 shrink-0 text-ek-purple" />
                  <span className="text-xs text-ek-ink/80">{m.name}</span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <span
                    className={`px-1.5 py-0.5 text-[0.6rem] font-semibold ${
                      m.impact === "high" ? "bg-ek-purple/10 text-ek-purple" : "bg-ek-canvas text-ek-ink/65"
                    }`}
                  >
                    {m.impact}
                  </span>
                  <span className="bg-ek-canvas px-1.5 py-0.5 text-[0.6rem] text-ek-ink/65">
                    {EFFORT_LABEL[m.effort] || m.effort} · {m.duration_weeks}w
                  </span>
                  {canSeed &&
                    (seeded[i] ? (
                      <span
                        data-testid={`move-seeded-${i}`}
                        className="flex items-center gap-0.5 bg-emerald-100 px-1.5 py-0.5 text-[0.6rem] font-semibold text-emerald-700"
                      >
                        <Check size={10} /> Added
                      </span>
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            disabled={seeding === i}
                            data-testid={`use-move-${i}`}
                            title="Add this move to a playbook"
                            className="flex items-center gap-0.5 rounded-sm bg-ek-purple px-1.5 py-0.5 text-[0.6rem] font-semibold text-white transition-colors hover:bg-ek-purple-deep disabled:opacity-50"
                          >
                            <Plus size={10} /> {seeding === i ? "…" : "Use"} <ChevronDown size={9} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                          <DropdownMenuItem
                            data-testid={`use-move-new-${i}`}
                            onClick={() => applyMove(m, i, null)}
                            className="text-xs font-medium"
                          >
                            <Plus size={12} className="mr-1.5 text-ek-purple" /> New playbook (assign to me)
                          </DropdownMenuItem>
                          {playbooks.length > 0 && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-[0.6rem] uppercase tracking-wide text-ek-ink/65">
                                Append to existing
                              </DropdownMenuLabel>
                              {playbooks.map((p) => (
                                <DropdownMenuItem
                                  key={p.playbook_id}
                                  data-testid={`append-to-${p.playbook_id}`}
                                  onClick={() => applyMove(m, i, p.playbook_id)}
                                  className="text-xs"
                                >
                                  <ArrowUpRight size={12} className="mr-1.5 text-ek-ink/65" />
                                  <span className="truncate">{p.title}</span>
                                </DropdownMenuItem>
                              ))}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
